import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { RiRegisteredLine } from "react-icons/ri";
import { toast } from 'react-toastify';
import { updateProduct } from '../../services/productService';
import { getCategories } from '../../services/categoryService';
import { getWarehouses } from '../../services/warehouseService';
import { getDepartments } from '../../services/departmentService';
import SerialNumberBadge from '../../components/product/SerialNumberBadge';
import Loader from '../../loader/Loader';
import { useNavigate, useLocation } from 'react-router-dom';
import { showErrorMessage, showSuccessMessage, showWarningMessage } from '../../utilis/CustomMessages';
import styles from './ProductForm.module.css'; 


const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UpdateProduct = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState(() => {
        const productBeingEdited = location.state?.product;
        if (productBeingEdited) {
            const initialData = { ...productBeingEdited }; // Copy product data

            // Ensure category, subcategory, warehouse, department store IDs for select fields
            initialData.category = productBeingEdited.category?._id || productBeingEdited.category || "";
            initialData.subcategory = productBeingEdited.subCategory?._id || productBeingEdited.subCategory || "";
            initialData.warehouse = productBeingEdited.warehouse?._id || productBeingEdited.warehouse || "";
            initialData.department = productBeingEdited.department?._id || productBeingEdited.department || "";

            // Initialize serialNumberNotes properly
            if (initialData.managedBySerial) {
                if (!Array.isArray(initialData.serialNumberNotes) || initialData.serialNumberNotes.length === 0) {
                    // If serialNumberNotes is missing/empty, derive from serialNumber (array of strings)
                    initialData.serialNumberNotes = (initialData.serialNumber || []).map(snValue => {
                        // This logic is for backward compatibility where serialNumberNotes might not exist
                        const existingNoteEntry = Array.isArray(productBeingEdited.serialNumberNotes)
                            ? productBeingEdited.serialNumberNotes.find(n => n.value === snValue)
                            : null;
                        return { 
                            value: snValue, 
                            notes: existingNoteEntry ? existingNoteEntry.notes : '',
                            functionalStatus: existingNoteEntry ? (existingNoteEntry.functionalStatus || '') : ''
                        };
                    });
                } else {
                    // If serialNumberNotes exists, ensure every item has a functionalStatus
                    initialData.serialNumberNotes = initialData.serialNumberNotes.map(note => ({
                        ...note,
                        functionalStatus: note.functionalStatus || ''
                    }));
                }
                // Ensure serialNumber (array of strings) is consistent with serialNumberNotes
                initialData.serialNumber = initialData.serialNumberNotes.map(snObj => snObj.value);
                initialData.quantity = initialData.serialNumberNotes.length; // Quantity is count of serials
            } else {
                initialData.serialNumberNotes = [];
                initialData.serialNumber = [];
                initialData.serialNumberAvailable = []; // Clear if not managed by serial
            }
            // Ensure militaryOrDualUse and hsCode are set
            initialData.militaryOrDualUse = productBeingEdited.militaryOrDualUse || "";
            initialData.hsCode = productBeingEdited.hsCode || "";
            
            return initialData;
        }
        // Fallback initial state (should ideally not be reached due to navigation guard)
        return {
            productName: "", productCode: "", managedBySerial: false, quantity: "",
            quantityAvailable: "", serialNumber: [], serialNumberAvailable: [],
            serialNumberNotes: [], weight: "", weightUnit: "kg", price: "", warehouse: "", 
            category: "", subcategory: "", department: "", notes: "",
            militaryOrDualUse: "", hsCode: ""
        };
    });
    const [newSerial, setNewSerial] = useState("");
    const [newSerialNote, setNewSerialNote] = useState(""); // For adding new serials with notes
    const [newSerialFunctionalStatus, setNewSerialFunctionalStatus] = useState("");
    const [activeOrders, setActiveOrders] = useState([]);
    const [originalQuantity, setOriginalQuantity] = useState(0);
    const [categories, setCategories] = useState([]);
    const [subcategoriesForSelectedCategory, setSubcategoriesForSelectedCategory] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const [departments, setDepartments] = useState([]);

    const [editingSerialIndex, setEditingSerialIndex] = useState(null);
    const [currentEditNote, setCurrentEditNote] = useState("");
    const [currentEditFunctionalStatus, setCurrentEditFunctionalStatus] = useState("");

    useEffect(() => {
        if (!location.state?.product) {
            navigate('/product-maintenance');
            toast.error("No product data provided for update.");
            return;
        }

        const product = location.state.product;
        setOriginalQuantity(product.quantity);
        fetchActiveOrders(product._id);

        const fetchDropdownData = async () => {
            try {
                const [categoriesData, warehousesData, departmentsData] = await Promise.all([ // Renamed for clarity
                    getCategories(),
                    getWarehouses(),
                    getDepartments()
                ]);
                setCategories(categoriesData);
                setWarehouses(warehousesData);
                setDepartments(departmentsData);

                // After categories are fetched, and if a category is pre-selected in formData,
                // populate its subcategories.
                if (formData.category && categoriesData.length > 0) {
                    const selectedCategoryFromData = categoriesData.find(cat => cat._id === formData.category);
                    if (selectedCategoryFromData && selectedCategoryFromData.subcategories && selectedCategoryFromData.subcategories.length > 0) {
                        setSubcategoriesForSelectedCategory(selectedCategoryFromData.subcategories);
                    } else {
                        setSubcategoriesForSelectedCategory([]);
                    }
                }
            } catch (error) {
                toast.error('Failed to fetch required data');
            }
        };
        fetchDropdownData();
    }, [location, navigate, formData.category]); // formData.category added as dependency to re-run if it changes programmatically

    const fetchActiveOrders = async (productId) => {
        try {
            const response = await axios.get(`${BACKEND_URL}/api/orders/getorders`);
            const activeOrders = response.data.filter(order => 
                (order.orderStatus === 'committed' || order.orderStatus === 'issued') &&
                order.products.some(p => p.product === productId)
            );
            setActiveOrders(activeOrders);
        } catch (error) {
            toast.error("Failed to fetch active orders");
            console.error("Error fetching orders:", error);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        
        if (name === 'quantity') {
            handleQuantityChange(e);
            return;
        }

        if (type === 'checkbox') {
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else if (name === 'price' || name === 'weight') {
            const numValue = value.replace(/[^0-9.]/g, '');
            setFormData(prev => ({ ...prev, [name]: numValue }));
        } else if (name === 'category') {
            const selectedCat = categories.find(cat => cat._id === value);
            setFormData(prev => ({
                ...prev,
                category: value,
                subcategory: "" // Reset subcategory when category changes
            }));
            if (selectedCat && selectedCat.subcategories && selectedCat.subcategories.length > 0) {
                setSubcategoriesForSelectedCategory(selectedCat.subcategories);
            } else {
                setSubcategoriesForSelectedCategory([]);
            }
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
    };

    const handleQuantityChange = (e) => {
        const newQuantity = parseInt(e.target.value) || 0;
        
        setFormData(prev => ({
            ...prev,
            quantity: newQuantity,
        }));
    };

    const handleSerialNumberChange = (e) => {
        setNewSerial(e.target.value);
    };

    const handleNewSerialNoteChange = (e) => {
        setNewSerialNote(e.target.value);
    };

    const addSerialNumber = () => {
        if (newSerial.trim() === "") {
            showErrorMessage('Invalid Input', 'Serial number cannot be empty.');
            return;
        }
        
        const serialValue = newSerial.trim();
        const noteValue = newSerialNote.trim();

        if (formData.serialNumberNotes && formData.serialNumberNotes.some(snObj => snObj.value === serialValue)) {
            showErrorMessage(
                'Duplicate Serial',
                'This serial number is already added to the product.'
            );
            return;
        }

        const statusToAdd = newSerialFunctionalStatus || "REAL FUNCTIONAL";

        setFormData(prev => ({
            ...prev,
            serialNumber: [...(prev.serialNumber || []), serialValue],
            serialNumberNotes: [...(prev.serialNumberNotes || []), { 
                value: serialValue, 
                notes: noteValue,
                functionalStatus: statusToAdd
            }],
            serialNumberAvailable: [...(prev.serialNumberAvailable || []), serialValue], // Assume new serials are available
            quantity: (prev.serialNumberNotes || []).length + 1,
            quantityAvailable: (prev.serialNumberAvailable || []).length + 1
        }));
        setNewSerial("");
        setNewSerialNote("");
        setNewSerialFunctionalStatus("");
    };

    const removeSerialNumber = async (index) => {
        const serialToRemoveObj = formData.serialNumberNotes[index];
        if (!serialToRemoveObj) return;
        const serialToRemoveValue = serialToRemoveObj.value;

        const ordersUsingSerial = activeOrders.filter(order => {
            return order.products.some(product => {
                const hasSerial = product.serialNumbers && 
                                product.serialNumbers.includes(serialToRemoveValue) &&
                                product.product === formData._id;
                if (hasSerial) {
                    console.log(`Serial ${serialToRemoveValue} found in order ${order.orderNumber}`);
                }
                return hasSerial;
            });
        });
    
        if (ordersUsingSerial.length > 0) {
            const orderNumbers = ordersUsingSerial.map(order => order.orderNumber).join(", ");
            
            try {
                showWarningMessage( // No need to await showWarningMessage if it's not returning a promise that resolves on user action
                    'Serial Number In Use',
                    `This serial number (${serialToRemoveValue}) is currently in use in the following orders: ${orderNumbers}. Cannot remove serial numbers that are actively being used.`
                );
            } catch (error) {
                console.error('Error showing warning message:', error);
            }
            return;
        }
    
        setFormData(prev => {
            const updatedSerialNumberNotes = prev.serialNumberNotes.filter((_, idx) => idx !== index);
            const updatedSerialNumbers = updatedSerialNumberNotes.map(snObj => snObj.value);
            const updatedAvailableSerials = prev.serialNumberAvailable.filter(s => s !== serialToRemoveValue);

            return {
                ...prev,
                serialNumber: updatedSerialNumbers,
                serialNumberNotes: updatedSerialNumberNotes,
                serialNumberAvailable: updatedAvailableSerials,
                quantity: updatedSerialNumberNotes.length,
                quantityAvailable: updatedAvailableSerials.length
            };
        });

        // Adjust editingSerialIndex if necessary
        if (editingSerialIndex === index) {
            setEditingSerialIndex(null);
            setCurrentEditNote("");
        } else if (editingSerialIndex !== null && editingSerialIndex > index) {
            setEditingSerialIndex(editingSerialIndex - 1);
        }
    };

    const handleEditSerialNumberData = (index) => {
        setEditingSerialIndex(index);
        setCurrentEditNote(formData.serialNumberNotes[index].notes || "");
        setCurrentEditFunctionalStatus(formData.serialNumberNotes[index].functionalStatus || "");
    };

    const handleSaveSerialNumberData = (index) => {
        const statusToSave = currentEditFunctionalStatus || "REAL FUNCTIONAL";
        setFormData(prev => {
            const updatedNotes = [...prev.serialNumberNotes];
            updatedNotes[index] = { 
                ...updatedNotes[index], 
                notes: currentEditNote,
                functionalStatus: statusToSave
            };
            return { ...prev, serialNumberNotes: updatedNotes };
        });
        setEditingSerialIndex(null);
        setCurrentEditNote("");
        setCurrentEditFunctionalStatus("");
    };
    const handleCancelEditSerialNumberData = () => {
        setEditingSerialIndex(null);
        setCurrentEditNote("");
        setCurrentEditFunctionalStatus("");
    };

    const validateForm = async () => {
        if (!formData.productName || !formData.productCode || !formData.quantity || 
            !formData.weight || !formData.price || !formData.department || 
            !formData.warehouse || !formData.category || !formData.weightUnit) {
            toast.error("Please fill all required fields.");
            return false;
        }
    
        const quantityNum = Number(formData.quantity);
        const priceNum = Number(formData.price);
        const weightNum = Number(formData.weight);
    
        // Basic numeric validation
        if (isNaN(quantityNum) || quantityNum < 0) {
            showErrorMessage('Invalid Input', "Please enter a valid quantity.");
            return false;
        }
    
        if (isNaN(priceNum) || priceNum <= 0) {
            showErrorMessage('Invalid Input', "Please enter a valid price.");
            return false;
        }
    
        if (isNaN(weightNum) || weightNum <= 0) {
            showErrorMessage('Invalid Input', "Please enter a valid weight.");
            return false;
        }
    
        // Special handling for serial-managed products
        if (formData.managedBySerial) {
            // Get all serials currently in use by active orders
            const serialsInUse = new Set();
            activeOrders.forEach(order => {
                order.products.forEach(p => {
                    if (p.product === formData._id && p.serialNumbers) {
                        p.serialNumbers.forEach(serial => serialsInUse.add(serial));
                    }
                });
            });
    
            // Check if all serials in use are still present in the product's serial numbers
            const missingSerials = Array.from(serialsInUse).filter(
                serial => !formData.serialNumberNotes.some(snObj => snObj.value === serial)
            );
    
            if (missingSerials.length > 0) {
                showErrorMessage(
                    'Invalid Serial Number Removal',
                    `Cannot remove the following serial numbers as they are in use: ${missingSerials.join(", ")}`
                );
                return false;
            }
    
            // At this point, we know all serials in use are preserved
            // We don't need to check quantity against active orders because
            // we've already verified we're only removing unused serials
            
            // Just verify the quantities match the serial numbers
            if (formData.serialNumberNotes.length !== quantityNum) {
                showErrorMessage(
                    'Quantity Mismatch',
                    'The number of serial numbers must match the total quantity.'
                );
                return false;
            }
        } else {
            // For non-serial products, check quantity reduction against active orders
            if (quantityNum < originalQuantity) {
                const ordersUsingProduct = activeOrders.filter(order => 
                    order.products.some(p => p.product === formData._id)
                );
    
                if (ordersUsingProduct.length > 0) {
                    const orderNumbers = ordersUsingProduct.map(order => order.orderNumber).join(", "); // Removed await here
                    await showWarningMessage(
                        'Cannot Reduce Quantity',
                        `This product is currently in use in the following orders: ${orderNumbers}. 
                         Please handle these orders first before reducing the quantity.`
                    );
                    return false;
                }
            }
        }
    
        return true;
    };

    const updateExistingProduct = async (e) => {
        e.preventDefault();
        
        if (!(await validateForm())) return;
    
        let productData;
        if (formData.managedBySerial) {
            // For serial-managed products, use the serial numbers to determine quantities
            productData = {
                ...formData,
                quantity: formData.serialNumberNotes.length,
                quantityAvailable: formData.serialNumberAvailable.length,
                serialNumber: formData.serialNumberNotes.map(sn => sn.value), // Array of strings
                serialNumberNotes: formData.serialNumberNotes, // Full objects with notes
                // serialNumberAvailable is already managed by add/remove functions
            };
        } else {
            // For non-serial products, use the quantity field
            const currentQuantity = Number(formData.quantity);
            const currentQuantityAvailable = Number(formData.quantityAvailable);
            const quantityDiff = currentQuantity - originalQuantity;
            const newQuantityAvailable = Math.max(0, currentQuantityAvailable + quantityDiff);
            
            productData = {
                ...formData,
                quantity: currentQuantity,
                quantityAvailable: newQuantityAvailable,
                serialNumber: [], // Clear for non-serial
                serialNumberAvailable: [], // Clear for non-serial
                serialNumberNotes: [] // Clear for non-serial
            };
        }
    
        setIsLoading(true);
        try {
            await updateProduct(formData._id, productData);
            showSuccessMessage(
                'Success',
                'Product Updated Successfully'
            );
            navigate('/product-maintenance');
        } catch (error) {
            showErrorMessage(
                'Update Failed',
                error.response?.data?.message || error.message || 'Failed to update product.'
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={styles.formContainer}>
            {isLoading && <Loader />}
            <div className={styles.formCard}>
            <div className={styles.topLine}></div>
            <button 
              className={styles.backButton}
              onClick={() => navigate('/product-maintenance')}
            >
              ← Back
            </button>
                <div className={styles.cardHeader}>
                    <RiRegisteredLine size={28} />
                    <h2 className={styles.title}>Update Product</h2>
                </div>

                <form onSubmit={updateExistingProduct}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Product Name
                            </label>
                            <input 
                                type="text" 
                                className={styles.formInput}
                                placeholder="e.g., Radio Device X500" 
                                name="productName" 
                                value={formData.productName} 
                                onChange={handleInputChange} 
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Product Code
                            </label>
                            <input 
                                type="text" 
                                className={styles.formInput}
                                placeholder="e.g., RDX-500" 
                                name="productCode" 
                                value={formData.productCode} 
                                onChange={handleInputChange} 
                            />
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Serial Number Management
                            </label>
                            <div className={styles.checkboxContainer}>
                                <input 
                                    type="checkbox" 
                                    name="managedBySerial" 
                                    checked={formData.managedBySerial} 
                                    onChange={handleInputChange}
                                    disabled={true} // Disabled in update mode
                                />
                                <span className={styles.checkboxLabel}>
                                    Track each unit with unique serial number
                                </span>
                            </div>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Quantity
                            </label>
                            <input 
                                type="number" 
                                className={styles.formInput}
                                placeholder="Enter total units" 
                                name="quantity" 
                                value={formData.quantity} 
                                onChange={handleInputChange}
                                disabled={formData.managedBySerial} 
                            />
                        </div>
                    </div>

                    {formData.managedBySerial && (
                        <div className={styles.formRow}>
                            <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                                <label className={styles.formLabel}>Serial Numbers</label>
                                <div className={styles.serialContainer}>
                                    <div className={styles.serialInputRow}>
                                        <input
                                            type="text"
                                            className={`${styles.formInput} ${styles.serialValueInput}`}
                                            placeholder="Enter serial number"
                                            value={newSerial}
                                            onChange={handleSerialNumberChange}
                                        />
                                        <input
                                            type="text"
                                            className={`${styles.formInput} ${styles.serialNoteInput}`}
                                            placeholder="Note for serial (optional)"
                                            value={newSerialNote}
                                            onChange={handleNewSerialNoteChange}
                                        />
                                        <select
                                            className={`${styles.formInput} ${styles.serialStatusSelect}`}
                                            value={newSerialFunctionalStatus}
                                            onChange={(e) => setNewSerialFunctionalStatus(e.target.value)}
                                        >
                                            <option value="">Select serial status</option>
                                            <option value="REAL FUNCTIONAL">Real Functional</option>
                                            <option value="REAL NON FUNCTIONAL">Real Non Functional</option>
                                            <option value="MOCK UP">Mock Up</option>
                                        </select>
                                    </div>
                                    <button 
                                        type="button" 
                                        onClick={addSerialNumber}
                                        className={styles.addSerialBtn}
                                    >
                                        Add
                                    </button>
                                </div>
                                <div className={`${styles.serialTags} ${styles.largerSerialBadge}`}>
                                    {formData.serialNumberNotes && formData.serialNumberNotes.map((serialObj, idx) => (
                                        <div key={serialObj.value + idx} className={styles.serialListItem}>
                                            <SerialNumberBadge
                                                serialNumber={serialObj.value}
                                                onRemove={() => removeSerialNumber(idx)}
                                                isRemovable={!activeOrders.some(order => order.products.some(p => p.product === formData._id && p.serialNumbers && p.serialNumbers.includes(serialObj.value)))}
                                                note={serialObj.notes} // Pass note for title attribute
                                                onEditData={editingSerialIndex !== idx ? () => handleEditSerialNumberData(idx) : undefined} // Pass edit handler for hover button
                                            />
                                            {editingSerialIndex === idx ? (
                                                <div className={styles.editNoteContainer}>
                                                    <input 
                                                        type="text" 
                                                        value={currentEditNote} 
                                                        onChange={(e) => setCurrentEditNote(e.target.value)} 
                                                        className={styles.editNoteInput} 
                                                        placeholder="Note (optional)"
                                                        autoFocus 
                                                    />
                                                    <select
                                                        value={currentEditFunctionalStatus}
                                                        onChange={(e) => setCurrentEditFunctionalStatus(e.target.value)}
                                                        className={styles.editStatusSelect}
                                                    >
                                                        <option value="">Select serial status</option>
                                                        <option value="REAL FUNCTIONAL">Real Functional</option>
                                                        <option value="REAL NON FUNCTIONAL">Real Non Functional</option>
                                                        <option value="MOCK UP">Mock Up</option>
                                                    </select>
                                                    <button type="button" onClick={() => handleSaveSerialNumberData(idx)} className={styles.saveNoteButton}>Save</button>
                                                    <button type="button" onClick={handleCancelEditSerialNumberData} className={styles.cancelNoteButton}>Cancel</button>
                                                </div>
                                            ) : (
                                                <div className={styles.noteDisplayContainer}>
                                                    {/* Display note text only. The edit button is now on hover within SerialNumberBadge. */}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Weight and Unit
                            </label>
                            <div className={styles.inputGroup}>
                                <input 
                                    type="text" 
                                    className={`${styles.formInput} ${styles.weightInput}`}
                                    placeholder="Enter weight" 
                                    name="weight" 
                                    value={formData.weight} 
                                    onChange={handleInputChange}
                                />
                                <select 
                                    name="weightUnit" 
                                    value={formData.weightUnit} 
                                    onChange={handleInputChange}
                                    className={`${styles.formInput} ${styles.weightUnit}`}
                                >
                                    <option value="kg">Kilograms (kg)</option>
                                    <option value="g">Grams (g)</option>
                                    <option value="lb">Pounds (lb)</option>
                                </select>
                            </div>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Price
                            </label>
                            <div className={styles.inputGroup}>
                                <div className={styles.priceInputContainer}>
                                    <span className={styles.priceSymbol}>$</span>
                                    <input 
                                        type="text" 
                                        className={`${styles.formInput} ${styles.priceInput}`}
                                        placeholder="Enter price" 
                                        name="price" 
                                        value={formData.price} 
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={styles.formRowThreeColumns}>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Warehouse Location
                            </label>
                            <select 
                                name="warehouse" 
                                value={formData.warehouse} 
                                onChange={handleInputChange}
                                className={styles.formInput}
                            >
                                <option value="">Select warehouse</option>
                                {warehouses.map(warehouse => (
                                    <option key={warehouse._id} value={warehouse._id}>
                                        {warehouse.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Department
                            </label>
                            <select 
                                name="department" 
                                value={formData.department} 
                                onChange={handleInputChange}
                                className={styles.formInput}
                            >
                                <option value="">Select department</option>
                                {departments.map(department => (
                                    <option key={department._id} value={department._id}>
                                        {department.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={`${styles.formLabel} ${styles.requiredLabel}`}>
                                Product Category
                            </label>
                            <select 
                                name="category" 
                                value={formData.category} 
                                onChange={handleInputChange}
                                className={styles.formInput}
                            >
                                <option value="">Select category</option>
                                {categories.map(category => (
                                    <option key={category._id} value={category._id}>
                                        {category.name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {subcategoriesForSelectedCategory.length > 0 && (
                            <div className={styles.formGroup}>
                                <label className={styles.formLabel}>
                                    Product Subcategory
                                </label>
                                <select
                                    name="subcategory"
                                    value={formData.subcategory}
                                    onChange={handleInputChange}
                                    className={styles.formInput}
                                >
                                    <option value="">Select subcategory</option>
                                    {subcategoriesForSelectedCategory.map(sub => (
                                        <option key={sub._id} value={sub._id}>{sub.name}</option>
                                    ))}
                                </select>
                            </div>
                        )}

                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                Military or Dual Use
                            </label>
                            <select 
                                name="militaryOrDualUse" 
                                value={formData.militaryOrDualUse} 
                                onChange={handleInputChange}
                                className={styles.formInput}
                            >
                                <option value="">Select classification</option>
                                <option value="MILITARY">Military</option>
                                <option value="DUAL USE">Dual Use</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label className={styles.formLabel}>
                                HS Code
                            </label>
                            <input 
                                type="text" 
                                className={styles.formInput}
                                placeholder="Enter HS code" 
                                name="hsCode" 
                                value={formData.hsCode} 
                                onChange={handleInputChange} 
                            />
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={`${styles.formGroup} ${styles.fullWidth}`}>
                            <label className={styles.formLabel}>Additional Notes</label>
                            <textarea 
                                className={styles.textareaInput}
                                placeholder="Enter any additional product information" 
                                name="notes" 
                                value={formData.notes} 
                                onChange={handleInputChange}
                                rows="4"
                            />
                        </div>
                    </div>

                    <button type="submit" className={styles.submitBtn}>
                        Update Product
                    </button>
                </form>
            </div>
        </div>
    );
};

export default UpdateProduct;
